import {
  array,
  boolean,
  date,
  mixed,
  number,
  object,
  string,
  ValidationError,
} from "yup";

import { validateNonNegativeNumber } from "./communitiesConfigurationForm";

import type {
  CommunityConfigurationFormOwner,
  CommunityConfigurationFormValues,
  NumericFormValue,
} from "./communitiesConfigurationForm";
import type { ObjectSchema } from "yup";

const numericFormValueSchema = (label: string, required = false) =>
  mixed<NumericFormValue>()
    .defined()
    .test("non-negative-number", `${label} is invalid`, function (value) {
      const validationResult = validateNonNegativeNumber(
        value,
        label,
        required
      );
      return validationResult === true
        ? true
        : this.createError({ message: validationResult });
    });

const nullableRateSchema = (label: string) =>
  number()
    .nullable()
    .optional()
    .test("non-negative-rate", `${label} is invalid`, function (value) {
      const validationResult = validateNonNegativeNumber(value, label);
      return validationResult === true
        ? true
        : this.createError({ message: validationResult });
    });

const communitySchema = object({
  ctmhcId: string()
    .defined()
    .test(
      "active-community-ctmhc-id",
      "CTMHCID is required for active communities",
      function (value) {
        return !this.parent.isActive || Boolean(value.trim());
      }
    ),
  defaultAddendumDate: date()
    .typeError("Default addendum date must be a valid date")
    .nullable()
    .defined(),
  isActive: boolean().defined(),
  ownerClientId: string()
    .defined()
    .test(
      "active-community-owner",
      "Owner is required for active communities",
      function (value) {
        return !this.parent.isActive || Boolean(value);
      }
    ),
  state: string().defined(),
});

const ownerSchema = object({
  addressLine1: string().defined(),
  addressLine2: string().defined(),
  city: string().defined(),
  clientId: string().defined(),
  county: string().defined(),
  municipality: string().defined(),
  name: string().defined(),
  // Blank state is allowed for owners not assigned to active communities;
  // active-owner requirements are enforced in the relationship test below.
  state: object({
    label: string().defined(),
    value: string().defined(),
  })
    // FormAutocomplete emits null when cleared; normalize to a blank option.
    .transform((value) => value ?? { label: "", value: "" })
    .defined(),
  zipCode: string().defined(),
});

const otherLocalTaxSchema = object({
  name: string()
    .defined()
    .test("local-tax-name", "Local tax name is required", (value) =>
      Boolean(value.trim())
    ),
  rate: number()
    .transform((parsedValue, originalValue) =>
      originalValue === "" ? undefined : parsedValue
    )
    .typeError("Local tax rate must be a non-negative number")
    .required("Local tax rate is required")
    .min(0, "Local tax rate must be a non-negative number"),
});

const localTaxesSchema = object({
  cityName: string()
    .defined()
    .test(
      "city-name-for-rate",
      "City is required when a city tax rate is set",
      function (value) {
        return this.parent.cityTaxRate == null || Boolean(value.trim());
      }
    ),
  cityTaxRate: nullableRateSchema("City tax rate"),
  countyName: string()
    .defined()
    .test(
      "county-name-for-rate",
      "County is required when a county tax rate is set",
      function (value) {
        return this.parent.countyTaxRate == null || Boolean(value.trim());
      }
    ),
  countyTaxRate: nullableRateSchema("County tax rate"),
  otherLocalTaxes: array().of(otherLocalTaxSchema).defined(),
  ownerClientId: string().defined(),
});

const policyNumberSchema = object({
  ownerClientId: string().defined(),
  policyNumber: string().defined(),
});

const stateSchema = object({
  localTaxes: array().of(localTaxesSchema).defined(),
  policyNumbers: array().of(policyNumberSchema).defined(),
  stampingFee: numericFormValueSchema("Stamping fee rate"),
  state: string().defined(),
  stateTax: numericFormValueSchema("State tax rate"),
  surplusLinesTax: numericFormValueSchema("Surplus lines tax rate"),
});

const requiredActiveOwnerFields: ReadonlyArray<{
  field: keyof Pick<
    CommunityConfigurationFormOwner,
    "addressLine1" | "city" | "county" | "name" | "state" | "zipCode"
  >;
  label: string;
}> = [
  { field: "name", label: "Legal name" },
  { field: "addressLine1", label: "Address line 1" },
  { field: "city", label: "City" },
  { field: "county", label: "County" },
  { field: "state", label: "State" },
  { field: "zipCode", label: "ZIP code" },
];

const rentManagerLocationSchema = object({
  // Validated at the option level so the error lands on the autocomplete field.
  glAccount: object({
    label: string().defined(),
    value: mixed<NumericFormValue>().defined(),
  })
    .defined()
    .test(
      "gl-account-required",
      "General Ledger account is required",
      (glAccount) =>
        glAccount.value !== "" &&
        glAccount.value !== null &&
        glAccount.value !== undefined
    ),
  id: number().defined(),
  name: string().defined(),
});

export const communityConfigurationSchema = object({
  communities: array().of(communitySchema).defined(),
  owners: array().of(ownerSchema).defined(),
  policyDefaults: object({
    limits: object({
      animalLiabilityLimit: numericFormValueSchema("Animal liability limit"),
      blanketRentalResidentLiabilityLimit: numericFormValueSchema(
        "Blanket rental resident liability limit",
        true
      ),
      medicalPaymentsLimit: numericFormValueSchema("Medical payments limit"),
      premisesLiabilityLimit: numericFormValueSchema(
        "Premises liability limit"
      ),
    }).defined(),
    mgaFee: numericFormValueSchema("MGA fee", true),
    operatorFee: numericFormValueSchema("Operator fee", true),
    pricePerUnit: numericFormValueSchema("Price per unit", true),
  }).defined(),
  rentManagerLocations: array().of(rentManagerLocationSchema).defined(),
  rentManagerSettings: object({
    liveNotesEnabled: boolean().defined(),
    shouldWriteDefaultUDFValues: boolean().defined(),
  })
    .nullable()
    .defined(),
  states: array().of(stateSchema).defined(),
}).test(
  "community-configuration-relationships",
  "Community configuration relationships are invalid",
  function (values) {
    if (!values) return true;

    const validationErrors: ValidationError[] = [];
    const activeCtmhcIdCounts = new Map<string, number>();

    values.communities.forEach((community) => {
      const normalizedCtmhcId = community.ctmhcId.trim();
      if (!community.isActive || !normalizedCtmhcId) return;
      activeCtmhcIdCounts.set(
        normalizedCtmhcId,
        (activeCtmhcIdCounts.get(normalizedCtmhcId) ?? 0) + 1
      );
    });

    values.communities.forEach((community, communityIndex) => {
      const normalizedCtmhcId = community.ctmhcId.trim();
      if (
        community.isActive &&
        normalizedCtmhcId &&
        activeCtmhcIdCounts.get(normalizedCtmhcId)! > 1
      ) {
        validationErrors.push(
          this.createError({
            message: "Active community CTMHCIDs must be unique",
            path: `communities.${communityIndex}.ctmhcId`,
          })
        );
      }
    });

    const activeOwnerClientIds = new Set(
      values.communities
        .filter((community) => community.isActive && community.ownerClientId)
        .map((community) => community.ownerClientId)
    );

    values.owners.forEach((owner, ownerIndex) => {
      if (!activeOwnerClientIds.has(owner.clientId)) return;
      requiredActiveOwnerFields.forEach(({ field, label }) => {
        const ownerValue = owner[field];
        const fieldValue =
          typeof ownerValue === "string" ? ownerValue : ownerValue.value;
        if (fieldValue.trim()) return;
        validationErrors.push(
          this.createError({
            message: `${label} is required for active communities`,
            path: `owners.${ownerIndex}.${field}`,
          })
        );
      });
    });

    values.states.forEach((state, stateIndex) => {
      state.policyNumbers.forEach((policyNumber, policyNumberIndex) => {
        const isRequired = values.communities.some(
          (community) =>
            community.isActive &&
            community.state === state.state &&
            community.ownerClientId === policyNumber.ownerClientId
        );
        if (!isRequired || policyNumber.policyNumber.trim()) return;
        validationErrors.push(
          this.createError({
            message: "Policy number is required for active communities",
            path: `states.${stateIndex}.policyNumbers.${policyNumberIndex}.policyNumber`,
          })
        );
      });
    });

    return validationErrors.length
      ? new ValidationError(validationErrors)
      : true;
  }
) as ObjectSchema<CommunityConfigurationFormValues>;
